import debounce from 'debounce';
import React, {useEffect, useRef, useState} from 'react';
import ReactDOM from 'react-dom';

type Props = {
  html: string;
  className?: string;
};

export const HtmlPreview: React.FC<Props> = ({html, className}) => {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [container] = useState(document.createElement('div'));

  const setHeight = (frame: HTMLIFrameElement) => {
    if (!frame.contentDocument) return;
    frame.style.height = '0px';
    Object.assign(frame.style, {
      height: `${frame.contentDocument.body.scrollHeight + 5}px`
    });
  };

  // set iframe height on resize
  useEffect(() => {
    const frame = frameRef.current;
    if (frame?.contentWindow) {
      const frameWindow = frame.contentWindow;
      const onResize = debounce(setHeight, 50).bind(null, frame);
      let width = frameWindow.innerWidth;
      const resize = () => {
        // only update if width changes to avoid event loop
        if (width !== frameWindow.innerWidth) {
          width = frameWindow.innerWidth;
          onResize();
        }
      };
      frame.contentWindow.addEventListener('resize', resize, false);
      return () => frameWindow.removeEventListener('resize', resize, false);
    }
  }, []);

  // update iframe height when html changes
  useEffect(() => {
    const frame = frameRef.current;
    if (frame?.contentDocument) {
      const frameDocument = frame.contentDocument;
      frameDocument.body.appendChild(container);
      // fix scrollbar glitches
      Object.assign(frameDocument.body.style, {
        overflowY: 'hidden',
        margin: '0'
      });
      const onLoad = debounce(setHeight, 50).bind(null, frame);
      // set initial frame height (if no images)
      setHeight(frame);
      const images = frameDocument.body.getElementsByTagName('img');
      Array.from(images).forEach((img: HTMLImageElement) => {
        // update height on each img load
        img.addEventListener('load', onLoad, {once: true});
      });
    }
  }, [container, html]);

  return (
    <iframe
      title="iframe"
      ref={frameRef}
      style={{height: 0, overflow: 'hidden'}}
      className={className}>
      {ReactDOM.createPortal(<div dangerouslySetInnerHTML={{__html: html}} />, container)}
    </iframe>
  );
};
